import { useState, useCallback, useEffect } from 'react';
import { Document as DocxDocument, Packer, Paragraph, TextRun } from 'docx';
import html2pdf from 'html2pdf.js';
import { useToast } from '@chakra-ui/react';
import { useReadEvent, useUpdateHtmlMinutes, } from '../../services/online/calendar/calendar';
import useRouter from '../../hooks/router/useRouter';
import { ModulesEnum } from '../../enum/Modules';
import { useCompaniesContext } from '../../contexts/companies';
import moment from 'moment';
import { useCreateDocument, useReadDocuments, useReadDocumentTypes, useUpdateDocument, } from '../../services/online/repository/repository';
import { DocumentKindEnum, } from '../document-details/type';
export const useLogic = (editor) => {
    // State declarations
    const [isPublishing, setIsPublishing] = useState(false);
    const { params, goBack, push } = useRouter();
    const eventId = params?.eventId;
    const [eventShouldBeRecorded, setEventShouldBeRecorded] = useState(false);
    const [eventWillBeRecorded, setEventWillBeRecorded] = useState(false);
    const [minuteDocument, setMinuteDocument] = useState(null);
    console.log('eventId', eventId);
    const { mutate: updateHtmlMinutes, isLoading: isUpdatingHtmlMinutes } = useUpdateHtmlMinutes();
    const toast = useToast();
    const { data: event, isLoading: isEventLoading } = useReadEvent(eventId, {
        query: { enabled: !!eventId },
    });
    const { mutate: updateDocument, isLoading: isUpdatingDocument } = useUpdateDocument();
    const { mutate: createDocument, isLoading: isCreatingDocument } = useCreateDocument();
    const { modules, selectedEnrolment, selectedCompanyId } = useCompaniesContext();
    const { data: documentTypes } = useReadDocumentTypes({
        'company-id': selectedCompanyId,
    }, {
        query: {
            enabled: !!selectedCompanyId,
        },
    });
    const minuteTypeId = documentTypes?.find(documentType => documentType.name === '_FT_BOARDMINUTE')?.id;
    const { data: minutesDocuments, refetch: refetchDocuments, isLoading: isDocumentLoading, } = useReadDocuments({
        'company-id': selectedCompanyId,
        'document-types': [minuteTypeId],
        'event-id': eventId,
    }, {
        query: {
            enabled: !!selectedCompanyId && !!minuteTypeId && !!eventId,
        },
    });
    useEffect(() => {
        const minuteDocumentItem = minutesDocuments?.[0]?.items.filter(item => item.typeName === '_FT_BOARDMINUTE');
        console.log('minuteDocumentItem', minuteDocumentItem);
        if (minuteDocumentItem?.length) {
            setMinuteDocument(minuteDocumentItem[0]);
        }
    }, [minutesDocuments]);
    useEffect(() => {
        if (event && event.publishedOn) {
            const shouldBeRecorded = modules?.some(m => m.name === ModulesEnum.AI_MINUTE_BUILDER);
            setEventShouldBeRecorded(shouldBeRecorded);
            const willBeRecorded = shouldBeRecorded && !!event.leexiReference;
            setEventWillBeRecorded(willBeRecorded);
        }
    }, [event, modules]);
    const handleSave = useCallback(() => {
        if (editor) {
            updateHtmlMinutes({
                eventId,
                data: {
                    data: editor.getHTML(),
                },
            });
            toast({
                title: 'Content saved',
                status: 'success',
                duration: 2000,
            });
        }
    }, [editor, toast, eventId]);
    const today = new Date();
    const eventIsPast = moment(event?.endDate).isBefore(today);
    useEffect(() => {
        if (event?.htmlMinutes && editor) {
            editor.commands.setContent(event.htmlMinutes);
            toast({
                title: 'Minutes loaded',
                status: 'success',
                duration: 2000,
            });
        }
    }, [event?.htmlMinutes, editor, toast]);
    const exportPDF = useCallback(() => {
        if (editor) {
            const element = document.createElement('div');
            element.innerHTML = editor.getHTML();
            element.style.padding = '40px';
            element.style.width = '8.5in';
            element.style.fontSize = '12pt';
            // Copy styles from editor
            element.querySelectorAll('h1').forEach(h1 => {
                h1.style.fontSize = '2em';
                h1.style.fontWeight = 'bold';
                h1.style.marginBottom = '0.5em';
            });
            element.querySelectorAll('h2').forEach(h2 => {
                h2.style.fontSize = '1.5em';
                h2.style.fontWeight = 'bold';
                h2.style.marginBottom = '0.5em';
            });
            element.querySelectorAll('strong').forEach(strong => {
                strong.style.fontWeight = 'bold';
            });
            element.querySelectorAll('em').forEach(em => {
                em.style.fontStyle = 'italic';
            });
            element.querySelectorAll('ul').forEach(ul => {
                ul.style.listStyleType = 'disc';
                ul.style.marginLeft = '20px';
            });
            element.querySelectorAll('ol').forEach(ol => {
                ol.style.listStyleType = 'decimal';
                ol.style.marginLeft = '20px';
            });
            const opt = {
                margin: 0.5,
                filename: 'board-minutes.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                },
                jsPDF: {
                    unit: 'in',
                    format: 'letter',
                    orientation: 'portrait',
                },
            };
            html2pdf().set(opt).from(element).save();
            toast({
                title: 'PDF exported successfully',
                status: 'success',
                duration: 2000,
            });
        }
    }, [editor, toast]);
    const handlePublish = useCallback(async () => {
        if (editor) {
            try {
                setIsPublishing(true);
                // Generate the DOCX file
                const blob = await generateDocxBlob();
                if (!blob) {
                    toast({
                        title: 'Error generating document',
                        status: 'error',
                        duration: 2000,
                    });
                    return;
                }
                // Create a File object from the blob
                const fileName = `minutes-${event?.name || 'document'}-${new Date()
                    .toISOString()
                    .slice(0, 10)}.docx`;
                const docxFile = {
                    content: await new Promise(resolve => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result.split(',')[1]);
                        reader.readAsDataURL(blob);
                    }),
                    filename: fileName,
                    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                };
                // If we have existing minutes document, update it; otherwise create a new one
                if (minuteDocument) {
                    console.log('minuteDocument', minuteDocument);
                    const data = {
                        ...minuteDocument,
                        eventId: event.id,
                        file: docxFile,
                    };
                    await updateDocument({
                        data,
                    }, {
                        onSuccess: () => {
                            setIsPublishing(false);
                            refetchDocuments();
                            const params = {
                                documentKind: DocumentKindEnum?.DOCUMENT,
                                documentId: minuteDocument?.id,
                            };
                            push('/company/document-details', {
                                companyId: selectedCompanyId,
                                ...params,
                            });
                            toast({
                                title: 'Minutes updated successfully',
                                status: 'success',
                                duration: 2000,
                            });
                        },
                        onError: () => {
                            setIsPublishing(false);
                            toast({
                                title: 'Error updating minutes',
                                status: 'error',
                                duration: 2000,
                            });
                        },
                    });
                }
                else {
                    await createDocument({
                        data: {
                            companyId: selectedEnrolment?.companyId,
                            title: `Minutes - ${event?.name || 'Meeting'}`,
                            description: `Minutes for meeting on ${moment(event?.startDate).format('MMMM Do YYYY')}`,
                            eventId: event.id,
                            file: docxFile,
                            typeId: minuteTypeId,
                            // Add other required fields as needed
                        },
                    }, {
                        onSuccess: data => {
                            setIsPublishing(false);
                            refetchDocuments();
                            toast({
                                title: 'Minutes published successfully',
                                status: 'success',
                                duration: 2000,
                            });
                            const params = {
                                documentKind: DocumentKindEnum?.DOCUMENT,
                                documentId: data.value,
                            };
                            push('/company/document-details', {
                                companyId: selectedCompanyId,
                                ...params,
                            });
                        },
                        onError: () => {
                            setIsPublishing(false);
                            toast({
                                title: 'Error publishing minutes',
                                status: 'error',
                                duration: 2000,
                            });
                        },
                    });
                }
            }
            catch (error) {
                console.error('Error publishing minutes:', error);
                toast({
                    title: 'Error publishing minutes',
                    status: 'error',
                    duration: 2000,
                });
                setIsPublishing(false);
            }
        }
    }, [
        editor,
        event,
        eventId,
        minuteDocument,
        createDocument,
        selectedEnrolment,
        push,
        toast,
    ]);
    // Helper function to generate DOCX blob (modified from exportDOCX)
    const generateDocxBlob = useCallback(async () => {
        if (!editor)
            return null;
        try {
            const content = editor.getHTML();
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, 'text/html');
            // Helper function to process lists recursively to handle nested lists
            const processListItems = (listElement, indent = 720) => {
                const isOrdered = listElement.nodeName === 'OL';
                return Array.from(listElement.children).flatMap((li, index) => {
                    const paragraphs = [];
                    // Create text runs for the main list item content
                    const textRuns = [];
                    const prefix = isOrdered ? `${index + 1}. ` : '• ';
                    // Process content before any nested list
                    Array.from(li.childNodes).forEach(child => {
                        if (child.nodeType === Node.TEXT_NODE) {
                            if (child.textContent && child.textContent.trim()) {
                                textRuns.push(new TextRun({ text: child.textContent, size: 24 }));
                            }
                        }
                        else if (child.nodeName === 'STRONG' || child.nodeName === 'B') {
                            textRuns.push(new TextRun({
                                text: child.textContent || '',
                                size: 24,
                                bold: true,
                            }));
                        }
                        else if (child.nodeName === 'EM' || child.nodeName === 'I') {
                            textRuns.push(new TextRun({
                                text: child.textContent || '',
                                size: 24,
                                italics: true,
                            }));
                        }
                        else if (child.nodeName === 'BR') {
                            textRuns.push(new TextRun({ text: '', break: 1 }));
                        }
                        else if (child.nodeName !== 'UL' && child.nodeName !== 'OL') {
                            textRuns.push(new TextRun({
                                text: child.textContent || '',
                                size: 24,
                            }));
                        }
                    });
                    // Add the main list item paragraph
                    paragraphs.push(new Paragraph({
                        children: [new TextRun({ text: prefix, size: 24 }), ...textRuns],
                        spacing: { after: 200 },
                        indent: { left: indent },
                    }));
                    // Process any nested lists
                    const nestedLists = li.querySelectorAll(':scope > ul, :scope > ol');
                    nestedLists.forEach(nestedList => {
                        const nestedParagraphs = processListItems(nestedList, indent + 720);
                        paragraphs.push(...nestedParagraphs);
                    });
                    return paragraphs;
                });
            };
            // Process HTML nodes to DOCX elements
            const processNode = (node) => {
                if (node.nodeName === 'H1') {
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: node.textContent || '',
                                size: 48,
                                bold: true,
                            }),
                        ],
                        spacing: { after: 400 },
                    });
                }
                else if (node.nodeName === 'H2') {
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: node.textContent || '',
                                size: 36,
                                bold: true,
                            }),
                        ],
                        spacing: { after: 300 },
                    });
                }
                else if (node.nodeName === 'H3') {
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: node.textContent || '',
                                size: 32,
                                bold: true,
                            }),
                        ],
                        spacing: { after: 200 },
                    });
                }
                else if (node.nodeName === 'UL' || node.nodeName === 'OL') {
                    // Handle lists with potential nesting
                    return processListItems(node);
                }
                else if (node.nodeName === '#text') {
                    // Process text nodes and their parent elements for formatting
                    const textContent = node.textContent || '';
                    if (!textContent.trim())
                        return new Paragraph({ children: [] });
                    const parentElement = node.parentElement;
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: textContent,
                                size: 24,
                                bold: parentElement?.closest('strong, b') !== null,
                                italics: parentElement?.closest('em, i') !== null,
                            }),
                        ],
                    });
                }
                else if (node.nodeName === 'P') {
                    // Handle paragraphs that may contain mixed formatting
                    const element = node;
                    const textRuns = [];
                    // Process child nodes to preserve inline formatting
                    element.childNodes.forEach(child => {
                        if (child.nodeType === Node.TEXT_NODE) {
                            textRuns.push(new TextRun({
                                text: child.textContent || '',
                                size: 24,
                                bold: child.parentElement?.closest('strong, b') !== null,
                                italics: child.parentElement?.closest('em, i') !== null,
                            }));
                        }
                        else if (child.nodeName === 'STRONG' || child.nodeName === 'B') {
                            textRuns.push(new TextRun({
                                text: child.textContent || '',
                                size: 24,
                                bold: true,
                            }));
                        }
                        else if (child.nodeName === 'EM' || child.nodeName === 'I') {
                            textRuns.push(new TextRun({
                                text: child.textContent || '',
                                size: 24,
                                italics: true,
                            }));
                        }
                        else if (child.nodeName === 'BR') {
                            textRuns.push(new TextRun({
                                text: '',
                                break: 1,
                            }));
                        }
                    });
                    return new Paragraph({ children: textRuns });
                }
                else if (node.nodeName === 'STRONG' || node.nodeName === 'B') {
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: node.textContent || '',
                                size: 24,
                                bold: true,
                            }),
                        ],
                    });
                }
                else if (node.nodeName === 'EM' || node.nodeName === 'I') {
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: node.textContent || '',
                                size: 24,
                                italics: true,
                            }),
                        ],
                    });
                }
                else if (node.nodeName === 'BR') {
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: '',
                                break: 1,
                            }),
                        ],
                    });
                }
                else {
                    const element = node;
                    return new Paragraph({
                        children: [
                            new TextRun({
                                text: element.textContent || '',
                                size: 24,
                                bold: element.closest('strong, b') !== null,
                                italics: element.closest('em, i') !== null,
                            }),
                        ],
                    });
                }
            };
            const docElements = Array.from(doc.body.childNodes)
                .map(processNode)
                .flat();
            const wordDoc = new DocxDocument({
                sections: [
                    {
                        properties: {},
                        children: docElements,
                    },
                ],
            });
            // Return the blob instead of downloading it
            const blob = await Packer.toBlob(wordDoc);
            return blob;
        }
        catch (error) {
            console.error('Error generating DOCX:', error);
            return null;
        }
    }, [editor]);
    // Export DOCX for downloading (keep this functionality)
    const exportDOCX = useCallback(async () => {
        const blob = await generateDocxBlob();
        if (blob) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'board-minutes.docx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast({
                title: 'DOCX exported successfully',
                status: 'success',
                duration: 2000,
            });
        }
    }, [generateDocxBlob, toast]);
    const insertContent = useCallback((content) => {
        if (editor) {
            editor.chain().focus().insertContent(content).run();
            toast({
                title: 'Content inserted',
                status: 'success',
                duration: 2000,
            });
        }
    }, [editor, toast]);
    return {
        handleSave,
        exportPDF,
        exportDOCX,
        insertContent,
        event,
        eventShouldBeRecorded,
        eventWillBeRecorded,
        showPreviewMinutes: eventIsPast && !!event?.publishedOn,
        companyPictureUrl: selectedEnrolment?.company?.picture?.uri,
        goBack,
        push,
        handlePublish,
        isDocumentLoading,
        isPublishing,
    };
};
